import React from "react";

/**
 * Xchangely logomark — ascending chevron with an angelic halo.
 *
 * The chevron reads as "on-ramp" (upward movement) and as the apex of an "A"
 * for a-changely. The halo, suspended just above the apex, gives it the warm
 * "angelic" angle the brand was already nodding to — a soft gold ellipse
 * (3/4-view halo), not a dot. Reads as guardianship + ascent in one glyph.
 */
export const XLogo = ({ size = 28, className = "", invert = false }) => {
  const fg = invert ? "#4F46FF" : "#FFFFFF";
  const halo = "#FFC857";
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className={className} aria-hidden>
      <defs>
        <linearGradient id="xch-grad" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#6E66FF" />
          <stop offset="1" stopColor="#4F46FF" />
        </linearGradient>
        {/* Soft glow behind the halo so it reads as 'lit' rather than 'drawn' */}
        <radialGradient id="xch-halo-glow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor={halo} stopOpacity="0.55" />
          <stop offset="1" stopColor={halo} stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Background tile */}
      <rect width="32" height="32" rx="9" fill={invert ? "#FFFFFF" : "url(#xch-grad)"} />

      {/* Halo glow */}
      <ellipse cx="16" cy="6.6" rx="7.2" ry="2.3" fill="url(#xch-halo-glow)" />
      {/* Halo ring — 3/4-view ellipse, the angelic accent */}
      <ellipse
        cx="16" cy="6.6" rx="5.2" ry="1.45"
        fill="none"
        stroke={halo}
        strokeWidth="1.4"
        strokeLinecap="round"
      />

      {/* Twin ascending chevrons (kept from the prior mark) */}
      <path
        d="M9 22 L16 12 L23 22"
        stroke={fg}
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M9 26 L16 16 L23 26"
        stroke={fg}
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeOpacity="0.4"
        fill="none"
      />
    </svg>
  );
};

export const Brand = ({ size = 28, theme = "light" }) => {
  const dark = theme === "dark";
  return (
    <div className="flex items-center gap-2.5">
      <XLogo size={size} invert={dark} />
      <div className="leading-none">
        <div
          className={`font-display font-extrabold text-[1.05rem] tracking-tight ${
            dark ? "text-white" : "text-foreground"
          }`}
          style={{ letterSpacing: "-0.04em" }}
        >
          xchangely
        </div>
        <div
          className={`text-[10px] uppercase tracking-[0.22em] mt-0.5 ${
            dark ? "text-white/55" : "text-muted-foreground"
          }`}
        >
          on-ramp
        </div>
      </div>
    </div>
  );
};

export const AssetGlyph = ({ symbol, color = "#0B0F19", size = 36 }) => (
  <div
    className="flex items-center justify-center rounded-full font-display font-bold text-white shrink-0"
    style={{ width: size, height: size, backgroundColor: color, fontSize: size * 0.36, letterSpacing: "-0.04em" }}
  >
    {symbol?.slice(0, 3) || "?"}
  </div>
);


/**
 * BrandMonogram — the chevron + halo on its own (no tile, no wordmark).
 * Useful as decorative branding inside cards, empty states, page corners.
 */
export const BrandMonogram = ({ size = 48, className = "", color = "#4F46FF", halo = "#FFC857" }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className={className} aria-hidden>
    {/* Halo */}
    <ellipse cx="16" cy="6.6" rx="5.2" ry="1.45"
             fill="none" stroke={halo} strokeWidth="1.4" strokeLinecap="round" />
    {/* Chevrons */}
    <path d="M9 22 L16 12 L23 22" stroke={color}
          strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <path d="M9 26 L16 16 L23 26" stroke={color}
          strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"
          strokeOpacity="0.4" fill="none" />
  </svg>
);


/**
 * BrandWatermark — a large, soft monogram pinned to the corner of a page or
 * card. Decorative-only; intentionally pointer-events-none so it never
 * interferes with the interactive surface beneath.
 */
export const BrandWatermark = ({
  size = 280,
  className = "",
  opacity = 0.05,
  position = "bottom-right",
}) => {
  const pos = {
    "bottom-right": "right-[-40px] bottom-[-40px]",
    "top-right":    "right-[-40px] top-[-40px]",
    "bottom-left":  "left-[-40px] bottom-[-40px]",
    "top-left":     "left-[-40px] top-[-40px]",
  }[position] || "right-[-40px] bottom-[-40px]";
  return (
    <div
      className={`absolute ${pos} pointer-events-none select-none ${className}`}
      style={{ opacity }}
      aria-hidden
    >
      <BrandMonogram size={size} />
    </div>
  );
};
